import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import axios, { AxiosRequestConfig } from 'axios';
import useSWRMutation from 'swr/mutation';

import { useLogin } from '@hooks/auth';
import { useEmailSignup } from '@repo/ui/src/hooks/customer/useEmailSignup';
import { identify } from '@repo/ui/src/utils/identify';
import { accountCreated } from '@repo/ui/src/services/analytics';
import { initialValues } from '@repo/ui/src/modules/UI/constants';

export interface Arg {
  email: string;
  password: string;
  passwordConfirmation: string;
  firstName: string;
  lastName: string;
  terms: boolean;
  emailSubscribe: boolean;
  smsSubscribe: boolean;
  phoneNumber: string;
}

const { auth } = publicRuntimeConfig;

interface Key extends AxiosRequestConfig {
  login: ReturnType<typeof useLogin>['trigger'];
  marketingSignup: ReturnType<typeof useEmailSignup>['trigger'];
}

const fetcher = async (
  { login, marketingSignup, ...key }: Key,
  { arg: arg }: { arg: typeof initialValues.create },
) => {
  const { username, password, firstName, lastName, phoneNumber } = arg;
  await axios({
    ...key,
    data: {
      username,
      password,
      userAttributes: [
        { name: 'email', value: username },
        { name: 'custom:phone_number', value: phoneNumber ?? '' },
        { name: 'given_name', value: firstName },
        { name: 'family_name', value: lastName },
      ],
    },
  });

  identify({ email: username, first_name: firstName, last_name: lastName });

  accountCreated({
    email: username,
    first_name: firstName,
    last_name: lastName,
  });

  if (arg?.emailSubscribe || arg?.smsSubscribe) {
    await marketingSignup({
      emailSubscribe: arg?.emailSubscribe === true ? arg?.emailSubscribe : undefined,
      smsSubscribe: arg?.smsSubscribe === true ? arg?.smsSubscribe : undefined,
      email: arg?.username,
      phoneNumber: arg?.phoneNumber,
      firstName: arg?.firstName,
      lastName: arg?.lastName,
      sourceDetail: 'account_creation',
    });
  }

  await login({ ...arg, rememberMe: true });

  return arg;
};

export const useSignup = () => {
  const { trigger: login } = useLogin();
  const { trigger: marketingSignup } = useEmailSignup();

  return useSWRMutation(
    {
      url: `${auth?.url ?? ''}/register`,
      method: 'POST',
      login,
      marketingSignup,
    },
    fetcher,
  );
};
